import {json} from "p3";
import common from "./common";
import definitions from "./definitions";
import SaveData from "./game/SaveData";
import BossFightScene from "./scenes/BossFightScene";
import GameScene from "./scenes/GameScene";
import InitialAppScene from "./scenes/InitialAppScene";
import InventoryScene from "./scenes/InventoryScene";
import MapScene from "./scenes/MapScene";
import MinigameResultScene from "./scenes/MinigameResultScene";
import OutroScene from "./scenes/OutroScene";
import PauseScene from "./scenes/PauseScene";
import QuestScene from "./scenes/QuestScene";
import RateScene from "./scenes/RateScene";
import SettingsScene from "./scenes/SettingsScene";
import ShopScene from "./scenes/ShopScene";
import SlotsScene from "./scenes/SlotsScene";
import SplashScene from "./scenes/SplashScene";
import settings from "./settings";

export default class Application {
    constructor() {

        const saveData = new SaveData();
        common.saveData = saveData;

        saveData.createEmptySlot(0);
        saveData.setActiveSlot(0);

        P3.audio.mute(saveData.settings.muted);

        if (settings.APP) {
            common.config.language = common.saveData.settings.language;
        }
        if (saveData.settings.languageState) {
            common.tracking.setLanguageState(saveData.settings.languageState);
        }

        // tracking
        common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.PAGE_VIEW, {
            //pageName: common.tracking.url,
            pageviewevent: 1
        });
        common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.INTERACTION, {
            interaction: "app open",
            pageview_event: 1
        });
        
        if (settings.APP && saveData.areAllSlotsEmpty()) {
            this.showInitialAppScene();
        } else {
            this.showSplashScene();
        }

        //this.showOutroScene(1);

        //this.showBossFightScene(definitions.BOSS_ID.CENTICORE);

        // TODO DEBUG
        //definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.TUTORIAL;
        //this.showGameScene();

        //P3.audio.mute(true);

        // tracking
        common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.INTERACTION, {
            interaction: "game load duration",
            interactionevent: 1
        });
    }

    /**
     * @return {InitialAppScene}
     */
    showInitialAppScene() {
        const scene = new InitialAppScene();
        scene.signals.next.add(() => {
            this.showSplashScene();
        });
        P3.scene.show(scene);
        InitialAppScene.active = scene;
        return scene;
    }

    /**
     * @return {SplashScene}
     */
    showSplashScene() {
        const scene = new SplashScene();
        scene.signals.next.add(() => {
            common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.INTERACTION, {
                interaction: "play",
                interactionevent: 1,
                gamestart_event: 1
            });
            if (settings.PHASE == 1) {
                if (common.saveData.activeSlot.player.tutorial && !settings.SKIP_TUTORIAL) {
                    definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.TUTORIAL;
                } else {
                    definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.FOREST_CLEARING;
                }
                this.showGameScene();
            } else {
                this.showSlotsScene();
            }
        });
        scene.signals.settings.add(() => {
            scene.pauseTweens();
            this.showSettingsScene(scene);
        });
        P3.scene.show(scene);
        SplashScene.active = scene;
        return scene;
    }

    /**
    * @return {PauseScene}
    */
    showSettingsScene(splashScene) {
        const scene = new SettingsScene();
        scene.signals.previous.once(() => {
            splashScene.resumeTweens();
            P3.scene.remove();
        });
        scene.signals.next.once(() => {
            this.showSplashScene();
        });
        P3.scene.add(scene);
        SettingsScene.active = scene;
        return scene;
    }

    /**
     * @return {SlotsScene}
     */
    showSlotsScene() {
        const scene = new SlotsScene();
        scene.signals.next.add(() => {
            if (common.saveData.activeSlot.player.tutorial && !settings.SKIP_TUTORIAL) {
                definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.TUTORIAL;
            } else {
                definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.FOREST_CLEARING;
            }
            this.showGameScene();
        });
        scene.signals.previous.once(() => {
            this.showSplashScene();
        });
        P3.scene.show(scene);
        SlotsScene.active = scene;
        return scene;
    }

    /**
     * @return {GameScene}
     */
    showGameScene(startingSpawnId, onInitialLevelLoad) {
        const scene = new GameScene(startingSpawnId, onInitialLevelLoad);
        scene.signals.pause.add(() => {
            scene.pauseTweens();

            const pause = this.showPauseScene();
            pause.signals.next.once(() => {
                scene.resumeTweens();
                scene.resume();
            });
        });
        scene.signals.map.add(() => {
            this.showMapScene(scene);
        });
        scene.signals.quest.add((questLog) => {
            this.showQuestScene(questLog, scene);
        });
        scene.signals.inventory.add((inventory) => {
            this.showInventoryScene(inventory, scene);
        });
        scene.signals.shop.add((inventory) => {
            this.showShopScene(inventory, scene);
        });
        scene.signals.rate.add((inventory) => {
            this.showRateScene(inventory, scene);
        });
        scene.signals.boss.add((boss) => {
            scene.pauseTweens();

            const fight = this.showBossFightScene(boss);
            fight.signals.next.once((success) => {
                scene.resumeTweens();
                scene.completeBossFight(success);
            });
        });
        scene.signals.minigame.add((minigame) => {
            scene.pauseTweens();

            const result = this.showMinigameResultScene(minigame);
            result.signals.next.once(() => {
                scene.resumeTweens();
            });
        });
        scene.signals.outro.add((mainQuestIndex) => {
            this.showOutroScene(mainQuestIndex);
        });
        P3.scene.show(scene);
        GameScene.active = scene;
        return scene;
    }

    /**
     * @param {string} boss
     * @return {BossFightScene}
     */
    showBossFightScene(boss) {
        const scene = new BossFightScene(boss);
        scene.signals.pause.add(() => {
            scene.bgm && scene.bgm.pause();
            scene.pauseTweens();

            const pause = this.showPauseScene();
            pause.signals.next.once(() => {
                scene.bgm && scene.bgm.play();
                scene.resumeTweens();
            });
        });
        scene.signals.next.once((victory) => {
            P3.scene.remove();
        });
        P3.scene.add(scene, true);
        BossFightScene.active = scene;
        return scene;
    }

    /**
     * @return {PauseScene}
     */
    showPauseScene() {
        const scene = new PauseScene();
        scene.signals.next.once(() => {
            P3.scene.remove();
        });
        scene.signals.previous.once(() => {
            this.showSplashScene();
        });
        P3.scene.add(scene);
        PauseScene.active = scene;
        return scene;
    }

    /**
     * @param {GameScene} [game=null]
     * @returns {MapScene}
     */
    showMapScene(game = null) {
        const scene = new MapScene();
        scene.signals.next.add((location) => {
            game && game.loadLevel(location).then();
            P3.scene.remove();
        });
        scene.signals.previous.add(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        P3.scene.add(scene);
        MapScene.active = scene;
        return scene;
    }

    /**
     * @param {QuestManager} questManager
     * @returns {QuestScene}
     */
    showQuestScene(questManager, game = null) {
        const scene = new QuestScene(questManager);
        scene.signals.previous.once(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        scene.signals.locationJump.once((level) => {
            P3.scene.remove();
            if (game) {
                game.resume();
                game.loadLevel(level);
            }
        })
        P3.scene.add(scene);
        QuestScene.active = scene;
        return scene;
    }

    /**
     * @param {Inventory} inventory
     * @returns {InventoryScene}
     */
    showInventoryScene(inventory, game = null) {
        const scene = new InventoryScene(inventory);
        scene.signals.previous.add(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        scene.signals.next.add((id) => {
            P3.scene.remove();
            if(id == 'shop') {
                setTimeout(() => {
                    this.showShopScene(inventory, game);
                }, 50);
            }
        });
        P3.scene.add(scene);
        InventoryScene.active = scene;
        return scene;
    }

    /**
     * @param {QuestManager} questManager
     * @returns {QuestScene}
     */
    showShopScene(inventory, game) {
        const scene = new ShopScene();
        scene.signals.previous.once(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        scene.signals.next.add((id) => {
            P3.scene.remove();
            if(id == 'inventory') {
                setTimeout(() => {
                    this.showInventoryScene(inventory, game);
                }, 50);
            }
        });
        P3.scene.add(scene);
        ShopScene.active = scene;
        return scene;
    }

    /**
     * @param {Quest} minigame
     * @return {MinigameResultScene}
     */
    showMinigameResultScene(minigame) {
        const scene = new MinigameResultScene(minigame);
        scene.signals.next.once(() => {
            P3.scene.remove();
        });
        P3.scene.add(scene);
        MinigameResultScene.active = scene;
        return scene;
    }

    /**
     * @return {RateScene}
     */
    showRateScene() {

        if (!settings.APP || common.saveData.haveReachedRatePopUpLimit) {
            console.log('rate limit reached or not in app mode');
            return;
        }
        common.saveData.incrementRatePopUps();

        const scene = new RateScene();
        scene.signals.next.once(() => {
            P3.scene.remove();
        });
        P3.scene.add(scene);
        RateScene.active = scene;
        return scene;
    }

    /**
     * @returns {OutroScene}
     */
    showOutroScene(mainQuestIndex) {
        const scene = new OutroScene(mainQuestIndex);
        scene.signals.next.add(() => {
            
            if (GameScene.active.getMainQuestsComplete() === true) {
                const splashScene = this.showSplashScene();
                splashScene.completedMessageMode = true;
            } else {
                const gameScene = this.showGameScene(definitions.MAIN_QUESTS[mainQuestIndex-1].split("|")[0], () => {
                    setTimeout(() => {
                        this.showRateScene();
                    }, 1000);
                });
                gameScene.prepareIdleMessage();
            }
        });
        P3.scene.show(scene);
        return scene;
    }

    fadeTransition(a, b, scope) {
        scope = scope || window;

        const transition = new P3.FadeTransition(0x0);
        common.transitions.addChild(transition);

        transition.signals.in.once(function() {
            transition.out();
            a && a.call(scope);
        }, this);

        transition.signals.out.once(function() {
            transition.destroy();
            b && b.call(scope);
        }, this);

        transition.in();
    }
}